/* eslint-disable @typescript-eslint/naming-convention */
import {
  observable, set, computed, action,
} from 'mobx';
import NotificationsCollection from '../Notification/NotificationsCollection';
import { diContainer, serviceNames } from '../../../diContainer/diContainer';
import { RESTRICTION_ERRORS } from '../../../constants/Errors';
import { SHIPPING_NOTIFICATIONS } from '../../../constants/Notifications';

export enum SKU_TYPE {
  SE = 'SE',
  GiftWrap = 'GW',
  GiftCard = 'GC',
  Donation = 'DN',
}

export interface ICartItem {
  cardMessage?: string;
  discountedAppliedPrice?: number;
  productBaseId?: number;
  productSize?: string;
  discountedPrice?: number;
  discountedUnitPrice?: number;
  formattedDiscountedUnitPrice?: string;
  productRgnName?: string;
  totalPercentDiscount?: number;
  productRgnSubheading?: string;
  isOutOfStock?: boolean;
  releaseDate?: string;
  skuBaseId?: number;
  isHazmat?: boolean;
  isKitItem?: boolean;
  kitQuantity?: number;
  kitNames?: string[];
  kitDiscountedUnitPrice?: number;
  kitUnitPrice?: number;
  kitDiscountUnitPrice?: string;
  hasHazmatError?: false;
  images: string[];
  productCode?: string;
  appliedPrice?: number;
  formattedAppliedPrice?: string;
  formattedDiscountedAppliedPrice?: string;
  appliedTax?: number;
  itemQuantity?: number;
  offerCode?: string;
  unitPrice?: number;
  formattedUnitPrice?: string;
  isMonogrammedItem?: boolean;
  isMonogramDiscounted?: boolean;
  isMonogramItemDiscounted?: boolean;
  isDonationItem?: boolean;
  monogramPrice?: number;
  monogramUnitPrice?: number;
  formattedMonogramPrice?: string;
  monogramDiscountedUnitPrice?: number;
  monogramSkuPercentDiscount?: number;
  monogramSkuFormattedPrice?: string;
  monogramSkuDiscountPrice?: number;
  monogramTotalPercentDiscount?: number;
  formattedMonogramDiscountPrice?: string;
  replenishmentFrequency?: string;
  isAutoReplenish?: boolean;
  shadename?: string;
  isPreOrder?: boolean;
  hexValue?: string;
  url?: string;
  monogramSkuPrice?: number;
  formatted_discounted_price?: string;
  inventory_status?: number;
  isInactive?: number;
  isRefillable?: number;
  isSampleItem?: boolean;
  isGWPOfferItem?: boolean;
  isSoldOut?: number;
  itemType?: string;
  shaded?: number;
  skuType: SKU_TYPE;
  strength?: string;
  sub_line?: string;
  tax_rate?: number;
  thumbnail?: string;
  isComplimentaryGift?: boolean;
  monogramDiscountPrice?: number;
  isBopisGWPItem?: boolean;
  isCourierGWPItem?: boolean;
  isRewardItem: boolean;
  rewardPoints: string;
  estimatedDeliveryDate: string;
}

class CartItem {
  @observable cardMessage: string;

  @observable productBaseId: number;

  @observable productSize: string;

  @observable discountedAppliedPrice: number;

  @observable discountedUnitPrice: number;

  @observable formattedDiscountedUnitPrice: string;

  @observable productRgnName: string;

  @observable totalPercentDiscount: number;

  @observable productRgnSubheading: string;

  @observable isOutOfStock: boolean;

  @observable releaseDate: string;

  @observable skuBaseId: number;

  @observable isHazmat: boolean;

  @observable isKitItem: boolean;

  @observable kitQuantity: number;

  @observable kitNames: string[];

  @observable kitUnitPrice: number;

  @observable kitDiscountedUnitPrice: number;

  @observable images: string[];

  @observable productCode: string;

  @observable appliedPrice: number;

  @observable formattedAppliedPrice: string;

  @observable formattedDiscountedAppliedPrice: string;

  @observable appliedTax: number;

  @observable itemQuantity: number;

  @observable offerCode: string;

  @observable unitPrice: number;

  @observable formattedUnitPrice: string;

  @observable isMonogrammedItem: boolean;

  @observable isMonogramDiscounted: boolean;

  @observable isMonogramItemDiscounted: boolean;

  @observable isComplimentaryGift: boolean;

  @observable isSampleItem: boolean;

  @observable isGWPOfferItem: boolean;

  @observable monogramUnitPrice: number;

  @observable formattedMonogramPrice: string;

  @observable monogramSkuPercentDiscount: number;

  @observable monogramSkuFormattedPrice: string;

  @observable monogramSkuDiscountPrice: number;

  @observable monogramTotalPercentDiscount: number;

  @observable monogramDiscountPrice: number;

  @observable monogramDiscountedUnitPrice: number;

  @observable replenishmentFrequency: string;

  @observable isAutoReplenish: boolean;

  @observable shadename: string;

  @observable isPreOrder: boolean;

  @observable hexValue: string;

  @observable url: string;

  @observable monogramSkuPrice: number;

  @observable isDonationItem: boolean;

  @observable isBopisGWPItem: boolean;

  @observable isCourierGWPItem: boolean;

  @observable private notificationsCollection: NotificationsCollection;

  @observable skuType: SKU_TYPE;

  @observable isRewardItem: boolean;

  @observable rewardPoints: string;

  @observable estimatedDeliveryDate: string;

  constructor() {
    this.notificationsCollection = diContainer.get(serviceNames.notificationsCollection);
  }

  static fromJSON(data: ICartItem): CartItem {
    const cartItem = new CartItem();
    set(cartItem, data);

    return cartItem;
  }

  @action
  update(data: CartItem): void {
    set(this, data);
  }

  @computed
  get itemUnitPrice(): number {
    if (this.isMonogrammedItem) {
      return this.monogramSkuPrice;
    }
    if (this.isKitItem && !this.isMonogrammedItem) {
      return this.kitUnitPrice;
    }
    return this.unitPrice;
  }

  @computed
  get processedDiscountedPrice(): number {
    if (this.monogramSkuPercentDiscount) {
      return this.monogramSkuDiscountPrice;
    }
    if (this.isKitItem && !this.isMonogrammedItem) {
      if (this.totalPercentDiscount) {
        return this.kitDiscountedUnitPrice;
      }
    }
    return this.discountedUnitPrice;
  }

  @computed
  get itemTotalPrice(): number {
    if (this.isMonogrammedItem) {
      if (this.monogramSkuPercentDiscount) {
        return this.monogramSkuDiscountPrice;
      }
      return this.monogramSkuPrice;
    }
    if (this.totalPercentDiscount) {
      return this.discountedAppliedPrice;
    }
    return this.appliedPrice;
  }

  @computed
  get quantity(): number {
    if (this.isKitItem) {
      return this.kitQuantity;
    }
    return this.itemQuantity;
  }

  @computed
  get preOrderMessage(): string {
    const releaseDate = new Date(this.releaseDate);
    const releaseDay = releaseDate.getDate();
    const releaseMonth = releaseDate.getMonth() + 1;
    return `${releaseMonth}/${releaseDay}`;
  }

  @computed
  get isDiscounted(): boolean {
    if (this.isMonogrammedItem) {
      return this.isMonogramItemDiscounted;
    }
    return this.unitPrice > this.discountedUnitPrice;
  }

  @computed
  get engravingPrice(): number {
    if (this.monogramTotalPercentDiscount) {
      return this.monogramDiscountedUnitPrice;
    }
    return this.monogramUnitPrice;
  }

  @computed
  get hasHazmatError(): boolean {
    const addressHazmatError = this.notificationsCollection.hasNotification(RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS)
      || this.notificationsCollection.hasNotification(RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS_MILITARY)
      || this.notificationsCollection.hasNotification(SHIPPING_NOTIFICATIONS.PRODUCT_HAZMAT_RESTRICTION);
    return this.isHazmat && addressHazmatError;
  }
}

export default CartItem;
